import { useEffect, useState, type FormEvent } from 'react';
import { ROLE_LABELS, type Role } from '@cannery/shared';
import { useAuth } from '../../auth/AuthContext';
import { api, ApiError } from '../../lib/api';
import {
  Badge,
  Button,
  Card,
  ErrorBanner,
  Field,
  Modal,
  Spinner,
  inputCls,
} from '../../components/ui';
import { Icon } from '../../components/Icon';

interface UserDto {
  id: string;
  name: string;
  username: string;
  role: Role;
  branchId: string | null;
  branch: { code: string; name: string } | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

type RoleTone = 'indigo' | 'emerald' | 'amber';
const ROLE_TONE: Record<Role, RoleTone> = {
  ADMIN: 'indigo',
  WASHING_EMPLOYEE: 'emerald',
  FOLDING_EMPLOYEE: 'amber',
};

/** Variant of the input style that sizes to content (toolbar selects). */
const autoWidthCls = inputCls.replace('w-full', '');

/** Map API errors onto form fields (Zod details, conflicts, generic messages). */
function mapFieldErrors(error: ApiError): { fields: Record<string, string>; form?: string } {
  const fields: Record<string, string> = {};
  if (Array.isArray(error.details)) {
    for (const detail of error.details) {
      if (detail && typeof detail === 'object' && 'path' in detail) {
        const path = String((detail as { path: unknown }).path);
        if (!fields[path]) fields[path] = String((detail as { message: unknown }).message);
      }
    }
  }
  if (Object.keys(fields).length > 0) return { fields };
  if (error.status === 409) return { fields: { username: error.message } };
  return { fields, form: error.message };
}

function UserFormModal({
  mode,
  initial,
  onClose,
  onSaved,
}: {
  mode: 'create' | 'edit';
  initial?: UserDto;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { branches } = useAuth();
  const isEdit = mode === 'edit';
  const [name, setName] = useState(initial?.name ?? '');
  const [username, setUsername] = useState(initial?.username ?? '');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<Role>(initial?.role ?? 'WASHING_EMPLOYEE');
  const [branchId, setBranchId] = useState(initial?.branchId ?? branches[0]?.id ?? '');
  const [isActive, setIsActive] = useState(initial?.isActive ?? true);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function doSubmit() {
    if (busy) return;
    setBusy(true);
    setErrors({});
    setFormError(null);

    const payload: Record<string, unknown> = {
      name: name.trim(),
      role,
      branchId: role === 'ADMIN' ? null : branchId || null,
    };
    if (isEdit) {
      if (password) payload.password = password;
      payload.isActive = isActive;
    } else {
      payload.username = username.trim().toLowerCase();
      payload.password = password;
    }

    try {
      if (isEdit && initial) {
        await api.patch(`/users/${initial.id}`, payload);
      } else {
        await api.post('/users', payload);
      }
      onSaved();
      onClose();
    } catch (err) {
      if (err instanceof ApiError) {
        const mapped = mapFieldErrors(err);
        setErrors(mapped.fields);
        setFormError(mapped.form ?? null);
      } else {
        setFormError('Something went wrong. Please try again.');
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title={isEdit ? 'Edit employee' : 'New employee'}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={() => void doSubmit()} disabled={busy}>
            {busy ? <Spinner className="h-4 w-4" /> : null}
            {isEdit ? 'Save changes' : 'Create account'}
          </Button>
        </>
      }
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void doSubmit();
        }}
        className="space-y-4"
      >
        {formError && <ErrorBanner message={formError} />}
        <Field label="Full name" htmlFor="emp-name" error={errors.name}>
          <input
            id="emp-name"
            className={inputCls}
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            placeholder="e.g. Juan Dela Cruz"
          />
        </Field>
        <Field
          label="Username"
          htmlFor="emp-username"
          error={errors.username}
          hint={isEdit ? 'Usernames cannot be changed after creation.' : 'Used to sign in.'}
        >
          <input
            id="emp-username"
            className={inputCls}
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            readOnly={isEdit}
            disabled={isEdit}
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            required={!isEdit}
            placeholder="e.g. washing1-lh1"
          />
        </Field>
        <Field
          label={isEdit ? 'New password (optional)' : 'Password'}
          htmlFor="emp-password"
          error={errors.password}
          hint={isEdit ? 'Leave blank to keep the current password.' : 'At least 8 characters.'}
        >
          <input
            id="emp-password"
            type="password"
            autoComplete="new-password"
            className={inputCls}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required={!isEdit}
            placeholder={isEdit ? '••••••••' : 'At least 8 characters'}
          />
        </Field>
        <Field label="Role" htmlFor="emp-role" error={errors.role}>
          <select
            id="emp-role"
            className={inputCls}
            value={role}
            onChange={(e) => setRole(e.target.value as Role)}
          >
            {(['WASHING_EMPLOYEE', 'FOLDING_EMPLOYEE', 'ADMIN'] as Role[]).map((r) => (
              <option key={r} value={r}>
                {ROLE_LABELS[r]}
              </option>
            ))}
          </select>
        </Field>
        {role !== 'ADMIN' ? (
          <Field label="Assigned branch" htmlFor="emp-branch" error={errors.branchId}>
            <select
              id="emp-branch"
              className={inputCls}
              value={branchId}
              onChange={(e) => setBranchId(e.target.value)}
              required
            >
              <option value="" disabled>
                Select a branch…
              </option>
              {branches.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.code} — {b.name}
                </option>
              ))}
            </select>
          </Field>
        ) : (
          <p className="rounded-xl bg-indigo-50 px-3 py-2.5 text-sm text-indigo-700">
            Admin accounts are cross-branch and are not assigned to a branch.
          </p>
        )}
        {isEdit && (
          <label className="flex cursor-pointer items-center justify-between rounded-xl border border-slate-200 px-4 py-3">
            <span>
              <span className="block text-sm font-medium text-slate-800">Account active</span>
              <span className="block text-xs text-slate-500">
                Inactive accounts cannot sign in.
              </span>
            </span>
            <input
              type="checkbox"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
              className="h-6 w-6 rounded accent-indigo-600"
            />
          </label>
        )}
        {/* Enables implicit Enter-to-submit inside the modal */}
        <button type="submit" className="hidden" aria-hidden="true" tabIndex={-1} />
      </form>
    </Modal>
  );
}

function ConfirmModal({
  user,
  busy,
  onClose,
  onConfirm,
}: {
  user: UserDto;
  busy: boolean;
  onClose: () => void;
  onConfirm: () => void;
}) {
  const deactivating = user.isActive;
  return (
    <Modal
      title={deactivating ? 'Deactivate account?' : 'Reactivate account?'}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button variant={deactivating ? 'danger' : 'primary'} onClick={onConfirm} disabled={busy}>
            {busy ? <Spinner className="h-4 w-4" /> : null}
            {deactivating ? 'Deactivate' : 'Reactivate'}
          </Button>
        </>
      }
    >
      <p className="text-sm text-slate-600">
        {deactivating ? (
          <>
            <span className="font-semibold text-slate-900">{user.name}</span> will be signed out
            immediately and will not be able to sign in until reactivated.
          </>
        ) : (
          <>
            <span className="font-semibold text-slate-900">{user.name}</span> will be able to sign
            in again.
          </>
        )}
      </p>
    </Modal>
  );
}

export function EmployeesPage() {
  const { branchFilter } = useAuth();
  const [users, setUsers] = useState<UserDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState<'ALL' | Role>('ALL');
  const [includeInactive, setIncludeInactive] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<UserDto | null>(null);
  const [toggleTarget, setToggleTarget] = useState<UserDto | null>(null);
  const [toggleBusy, setToggleBusy] = useState(false);
  const [listError, setListError] = useState<string | null>(null);

  // debounce search
  useEffect(() => {
    const timer = setTimeout(() => setQuery(search.trim()), 250);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const params = new URLSearchParams();
    if (branchFilter !== 'ALL') params.set('branch', branchFilter);
    if (roleFilter !== 'ALL') params.set('role', roleFilter);
    if (query) params.set('q', query);
    params.set('includeInactive', String(includeInactive));

    void api
      .get<{ users: UserDto[] }>(`/users?${params.toString()}`)
      .then((res) => {
        if (!cancelled) {
          setUsers(res.users);
          setError(null);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof ApiError ? err.message : 'Failed to load employees.');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [branchFilter, roleFilter, query, includeInactive, refreshKey]);

  const refresh = () => setRefreshKey((k) => k + 1);

  async function confirmToggle() {
    if (!toggleTarget) return;
    setToggleBusy(true);
    try {
      await api.patch(`/users/${toggleTarget.id}`, { isActive: !toggleTarget.isActive });
      setToggleTarget(null);
      refresh();
    } catch (err) {
      setListError(err instanceof ApiError ? err.message : 'Failed to update account.');
      setToggleTarget(null);
    } finally {
      setToggleBusy(false);
    }
  }

  return (
    <section className="space-y-4" aria-label="Employees">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold tracking-tight text-slate-900">Employees</h2>
          <p className="mt-0.5 text-sm text-slate-500">
            Multiple accounts per role, each assigned to one branch.
            {branchFilter !== 'ALL' && ` Showing ${branchFilter} (branch filter).`}
          </p>
        </div>
        <Button size="lg" onClick={() => setCreating(true)}>
          <Icon name="plus" className="h-5 w-5" />
          New employee
        </Button>
      </div>

      {listError && <ErrorBanner message={listError} />}

      <Card className="p-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative min-w-55 flex-1">
            <Icon
              name="search"
              className="pointer-events-none absolute top-1/2 left-3 h-5 w-5 -translate-y-1/2 text-slate-400"
            />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name or username…"
              aria-label="Search employees"
              className={`${inputCls} pl-10`}
            />
          </div>
          <select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value as 'ALL' | Role)}
            aria-label="Filter by role"
            className={`${autoWidthCls} w-auto`}
          >
            <option value="ALL">All roles</option>
            <option value="WASHING_EMPLOYEE">Washing employees</option>
            <option value="FOLDING_EMPLOYEE">Folding employees</option>
            <option value="ADMIN">Admins</option>
          </select>
          <label className="flex h-12 cursor-pointer items-center gap-2 rounded-xl border border-slate-300 px-3 text-sm font-medium text-slate-600">
            <input
              type="checkbox"
              checked={includeInactive}
              onChange={(e) => setIncludeInactive(e.target.checked)}
              className="h-4 w-4 rounded accent-indigo-600"
            />
            Include inactive
          </label>
        </div>
      </Card>

      {error ? (
        <ErrorBanner message={error} />
      ) : loading ? (
        <div className="flex items-center justify-center gap-3 py-10 text-slate-500">
          <Spinner className="h-5 w-5 text-indigo-600" /> Loading employees…
        </div>
      ) : users.length === 0 ? (
        <Card className="flex flex-col items-center p-8 text-center">
          <Icon name="users" className="h-8 w-8 text-slate-300" />
          <p className="mt-2 font-medium text-slate-700">No employees found</p>
          <p className="mt-1 text-sm text-slate-500">Adjust the filters or create a new account.</p>
        </Card>
      ) : (
        <>
          {/* Desktop table */}
          <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-panel shadow-sm">
            <table className="w-full text-left text-sm">
              <thead className="bg-slate-50 text-xs font-semibold tracking-wide text-slate-500 uppercase">
                <tr>
                  <th className="px-4 py-3">Employee</th>
                  <th className="px-4 py-3">Role</th>
                  <th className="px-4 py-3">Branch</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Created</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {users.map((u) => (
                  <tr key={u.id} className="hover:bg-slate-100/70">
                    <td className="px-4 py-3">
                      <p className="font-semibold text-slate-900">{u.name}</p>
                      <p className="text-xs text-slate-500">@{u.username}</p>
                    </td>
                    <td className="px-4 py-3">
                      <Badge tone={ROLE_TONE[u.role]}>{ROLE_LABELS[u.role]}</Badge>
                    </td>
                    <td className="px-4 py-3">
                      {u.branch ? (
                        <Badge>{u.branch.code}</Badge>
                      ) : (
                        <span className="text-slate-400">Cross-branch</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {u.isActive ? (
                        <Badge tone="emerald">Active</Badge>
                      ) : (
                        <Badge tone="rose">Inactive</Badge>
                      )}
                    </td>
                    <td className="px-4 py-3 text-slate-500">
                      {new Date(u.createdAt).toLocaleDateString('en-PH')}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-2">
                        <Button size="sm" variant="secondary" onClick={() => setEditing(u)}>
                          Edit
                        </Button>
                        <Button
                          size="sm"
                          variant={u.isActive ? 'danger' : 'ghost'}
                          onClick={() => setToggleTarget(u)}
                        >
                          {u.isActive ? 'Deactivate' : 'Reactivate'}
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

        </>
      )}

      {creating && (
        <UserFormModal mode="create" onClose={() => setCreating(false)} onSaved={refresh} />
      )}
      {editing && (
        <UserFormModal
          mode="edit"
          initial={editing}
          onClose={() => setEditing(null)}
          onSaved={refresh}
        />
      )}
      {toggleTarget && (
        <ConfirmModal
          user={toggleTarget}
          busy={toggleBusy}
          onClose={() => setToggleTarget(null)}
          onConfirm={() => void confirmToggle()}
        />
      )}
    </section>
  );
}
